import { useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Camera, FileText, Loader2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { api } from "@/lib/api";

/**
 * PitchCaptureButton — outils de capture commerciale d'un graphe.
 *
 * 1. « Capture pour pitch » : PNG HD avec filigrane Noam, copié dans le
 *    presse-papier (fallback : téléchargement).
 * 2. « Page A4 » (si docTitle fourni) : page A4 PDF prête à envoyer —
 *    en-tête Noam, graphe, lecture stratégique 3Q (Impact / Urgence / Action),
 *    pied de page contact. Support de vente du « Diagnostic de trajectoire ».
 */

async function captureCanvas(targetId) {
  const el = document.getElementById(targetId);
  if (!el) throw new Error("Zone de capture introuvable.");
  return html2canvas(el, { scale: 2, backgroundColor: "#0A0A0F", useCORS: true, logging: false });
}

function watermark(canvas) {
  const ctx = canvas.getContext("2d");
  const fs = Math.max(18, Math.round(canvas.width * 0.011));
  ctx.font = `600 ${fs}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.32)";
  ctx.textAlign = "right";
  ctx.fillText("Noam · Constante Partners", canvas.width - fs, canvas.height - fs);
  return canvas;
}

async function buildA4Pdf({ canvas, docTitle, docSubtitle, threeQ, filename, clientName }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210;
  const M = 14; // marge
  const CW = W - 2 * M; // largeur utile
  const headerH = clientName ? 32 : 26;

  // ---- En-tête bandeau sombre ----
  doc.setFillColor(10, 10, 15);
  doc.rect(0, 0, W, headerH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  doc.text("NOAM", M, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 170);
  doc.text("by Constante Partners", M, 17.5);
  doc.setTextColor(239, 159, 39);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Diagnostic de trajectoire", W - M, 11, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 170);
  doc.text(new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }), W - M, 16.5, { align: "right" });

  if (clientName) {
    // Ruban « Préparé pour » — subtil, bordure orange
    doc.setDrawColor(239, 159, 39);
    doc.setLineWidth(0.3);
    doc.line(M, 23, W - M, 23);
    doc.setTextColor(239, 159, 39);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text("PRÉPARÉ POUR", M, 28.5);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    const safeName = doc.splitTextToSize(clientName, CW - 40)[0];
    doc.text(safeName, M + 26, 28.5);
  }

  // ---- Titre du document ----
  let y = headerH + 11;
  doc.setTextColor(20, 20, 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(docTitle, M, y);
  y += 6;
  if (docSubtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 120);
    const sub = doc.splitTextToSize(docSubtitle, CW);
    doc.text(sub, M, y);
    y += sub.length * 4 + 2;
  }

  // ---- Image du graphe ----
  const ratio = canvas.height / canvas.width;
  let imgW = CW;
  let imgH = imgW * ratio;
  const maxImgH = 130;
  if (imgH > maxImgH) {
    imgH = maxImgH;
    imgW = imgH / ratio;
  }
  const imgX = M + (CW - imgW) / 2;
  doc.setDrawColor(210, 210, 218);
  doc.setLineWidth(0.3);
  doc.rect(imgX - 0.6, y - 0.6, imgW + 1.2, imgH + 1.2);
  doc.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", imgX, y, imgW, imgH);
  y += imgH + 9;

  // ---- Lecture stratégique 3Q ----
  doc.setFillColor(239, 159, 39);
  doc.rect(M, y - 3.6, 1.4, 4.6, "F");
  doc.setTextColor(20, 20, 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Lecture stratégique", M + 4, y);
  y += 6;

  const blocks = threeQ
    ? [
        { label: "IMPACT", color: [55, 138, 221], text: [threeQ.narrative, threeQ.impact_display].filter(Boolean).join(" · ") },
        { label: "URGENCE", color: [239, 159, 39], text: threeQ.why_now || "Fenêtre d'action ouverte — à traiter avant le prochain cycle de décision." },
        { label: "ACTION", color: [29, 158, 117], text: threeQ.cta?.label ? `${threeQ.cta.label} — préparé et pré-rempli dans votre environnement Noam.` : "Ouvrez votre environnement Noam pour lancer l'action recommandée." },
      ]
    : [
        { label: "LECTURE", color: [127, 119, 221], text: "Cette cartographie est vivante : Noam la met à jour en croisant vos données internes et les signaux externes. Demandez une lecture stratégique complète (impact chiffré, urgence, plan d'action) lors d'un Diagnostic de trajectoire." },
      ];

  doc.setFontSize(8.5);
  for (const b of blocks) {
    if (y > 275) break;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(b.color[0], b.color[1], b.color[2]);
    doc.text(b.label, M, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(55, 55, 65);
    const lines = doc.splitTextToSize(b.text || "—", CW - 24);
    doc.text(lines, M + 24, y);
    y += Math.max(lines.length * 3.9, 5) + 2.5;
  }

  // ---- Pied de page ----
  doc.setDrawColor(225, 225, 230);
  doc.setLineWidth(0.2);
  doc.line(M, 285, W - M, 285);
  doc.setFontSize(7.5);
  doc.setTextColor(140, 140, 150);
  doc.text("Généré par Noam — le système nerveux décisionnel de votre organisation", M, 290);
  doc.setTextColor(100, 100, 110);
  doc.text("Constante Partners · Genève · contact@constante-partners.com", W - M, 290, { align: "right" });

  const slug = (clientName || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`${filename}${slug ? `-${slug}` : ""}-A4.pdf`);
}

export default function PitchCaptureButton({
  targetId,
  filename = "noam-capture",
  label = "Capture pour pitch",
  docTitle = "",
  docSubtitle = "",
  threeQEndpoint = "",
}) {
  const [busy, setBusy] = useState(false);
  const [busyA4, setBusyA4] = useState(false);
  const [askClient, setAskClient] = useState(false);
  const [clientName, setClientName] = useState(() => {
    try { return localStorage.getItem("noam.a4.clientName") || ""; } catch { return ""; }
  });

  const capture = async () => {
    setBusy(true);
    try {
      const canvas = watermark(await captureCanvas(targetId));
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      let copied = false;
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          copied = true;
        }
      } catch { /* fallback download */ }
      if (!copied) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${filename}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
      notify.success(copied
        ? "Capture HD copiée — collez-la directement dans votre proposition (Ctrl+V)."
        : "Capture HD téléchargée en PNG.");
    } catch {
      notify.error("Capture impossible sur ce graphe.");
    } finally {
      setBusy(false);
    }
  };

  const exportA4 = async (nameOverride) => {
    setAskClient(false);
    setBusyA4(true);
    const name = (nameOverride ?? clientName ?? "").trim();
    try {
      try { localStorage.setItem("noam.a4.clientName", name); } catch { /* storage unavailable */ }
      const canvas = watermark(await captureCanvas(targetId));
      let threeQ = null;
      if (threeQEndpoint) {
        try {
          const r = await api.get(threeQEndpoint);
          if (r.data && r.data.show !== false && r.data.narrative) threeQ = r.data;
        } catch { /* section fallback */ }
      }
      await buildA4Pdf({ canvas, docTitle: docTitle || "Cartographie Noam", docSubtitle, threeQ, filename, clientName: name });
      notify.success(name
        ? `Page A4 nominative prête — préparée pour ${name}.`
        : "Page A4 prête à envoyer — graphe + lecture stratégique, brandée Noam.");
    } catch {
      notify.error("Génération de la page A4 impossible.");
    } finally {
      setBusyA4(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        onClick={capture}
        disabled={busy}
        className="text-[11px] px-2.5 py-1.5 rounded border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/10 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
        data-testid="pitch-capture-btn"
        title="Capture HD du graphe avec filigrane Noam — idéale pour vos propositions clients"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} {label}
      </button>
      {docTitle && (
        <button
          onClick={() => setAskClient(true)}
          disabled={busyA4}
          className="text-[11px] px-2.5 py-1.5 rounded border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/10 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
          data-testid="pitch-a4-btn"
          title="Page A4 PDF prête à envoyer : graphe + lecture stratégique 3Q + branding Noam"
        >
          {busyA4 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Page A4
        </button>
      )}
      {askClient && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAskClient(false)}
          data-testid="pitch-a4-client-overlay"
        >
          <div
            className="w-full max-w-sm rounded-lg border border-white/10 bg-[#0A0A0F] p-5 space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Page A4 nominative</div>
              <div className="text-sm text-white mt-1 font-semibold">Pour qui préparez-vous ce document&nbsp;?</div>
              <p className="text-[11px] text-zinc-400 mt-1">Optionnel — le nom apparaîtra dans l&apos;en-tête «&nbsp;Préparé pour&nbsp;».</p>
            </div>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") exportA4(clientName); if (e.key === "Escape") setAskClient(false); }}
              autoFocus
              placeholder="Ex. Banque Cantonale Vaudoise"
              className="w-full text-sm px-3 py-2 rounded border border-white/15 bg-white/5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#EF9F27]/60"
              data-testid="pitch-a4-client-input"
              maxLength={80}
            />
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => exportA4("")}
                className="text-[11px] text-zinc-400 hover:text-white underline underline-offset-2"
                data-testid="pitch-a4-skip-btn"
              >
                Générer sans nom
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAskClient(false)}
                  className="text-[11px] px-3 py-1.5 rounded border border-white/15 text-zinc-300 hover:bg-white/5"
                  data-testid="pitch-a4-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={() => exportA4(clientName)}
                  className="text-[11px] px-3 py-1.5 rounded bg-[#EF9F27] text-black font-semibold hover:bg-[#EF9F27]/90"
                  data-testid="pitch-a4-confirm-btn"
                >
                  Générer la page A4
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
